import PlatformIcon from "@/components/Generic/PlatformIcon";
import { formatNumber } from "@/utils/format";
import { getTikTokColor } from "./chartUtils";

// Types for chart components
export interface TooltipPayload {
  color: string;
  dataKey: string;
  value: number;
}

export interface PieTooltipPayload {
  payload: {
    platform: string;
    views: number;
    percentage: number;
    color: string;
  };
}

export interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

// Custom tooltip component
export const CustomTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    // Calculate total for this date
    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          {new Date(label || "").toLocaleDateString()}
        </p>

        {/* Individual platform values */}
        {payload.map((entry: TooltipPayload, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor:
                  entry.dataKey === "tiktok" ? getTikTokColor() : entry.color,
              }}
            />
            <PlatformIcon platform={entry.dataKey} />
            <span className="capitalize text-gray-600 dark:text-gray-300">
              {entry.dataKey}:
            </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}

        {/* Total */}
        <div className="border-t border-gray-200 dark:border-gray-600 mt-2 pt-2">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <span className="font-semibold text-gray-600 dark:text-gray-300">
              Total:
            </span>
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(total)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom pie chart tooltip
export const PieTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: PieTooltipPayload[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor:
                data.platform === "tiktok" ? getTikTokColor() : data.color,
            }}
          />
          <PlatformIcon platform={data.platform} />
          <span className="capitalize text-gray-600 dark:text-gray-300">
            {data.platform}:
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatNumber(data.views)} views ({data.percentage}%)
          </span>
        </div>
      </div>
    );
  }
  return null;
};
